import { supabase, isSupabaseConfigured } from '../config/supabase';

/**
 * Get delivery settings from Supabase
 * @returns {Promise<Object>} Delivery settings object
 */
export const getDeliverySettings = async () => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  try {
    const { data, error } = await supabase
      .from('delivery_settings')
      .select('*')
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error fetching delivery settings:', error);
      return { data: null, error };
    }

    return { data: data || null, error: null };
  } catch (error) {
    console.error('Error in getDeliverySettings:', error);
    return { data: null, error };
  }
};

/**
 * Save or update delivery settings
 * @param {Object} formData - Delivery settings data
 * @returns {Promise<Object>} Saved delivery settings
 */
export const saveDeliverySettings = async (formData) => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  try {
    console.log('Starting to save delivery settings to Supabase...');

    // Prepare data for database
    const deliveryData = {
      delivery_fee: parseFloat(formData.deliveryFee) || 0,
      free_delivery_above: parseFloat(formData.freeDeliveryAbove) || 0,
      min_order_amount: parseFloat(formData.minOrderAmount) || 0,
      delivery_time_min: parseInt(formData.deliveryTimeMin) || 30,
      delivery_time_max: parseInt(formData.deliveryTimeMax) || 40
    };

    console.log('Data to save:', deliveryData);

    // Check if delivery settings already exists
    const { data: existing, error: checkError } = await supabase
      .from('delivery_settings')
      .select('id')
      .limit(1)
      .single();

    let result;
    let isUpdate = false;

    // If no existing record or error (PGRST116 means no rows), insert new
    if (!existing || checkError?.code === 'PGRST116') {
      console.log('No existing record found, inserting new delivery settings...');
      // Insert new record
      const { data, error } = await supabase
        .from('delivery_settings')
        .insert([deliveryData])
        .select()
        .single();

      if (error) {
        console.error('Error inserting delivery settings:', error);
        throw error;
      }
      result = data;
      console.log('✅ Delivery settings inserted successfully:', result);
    } else {
      console.log('Existing record found, updating delivery settings...', existing.id);
      isUpdate = true;
      // Update existing record
      const { data, error } = await supabase
        .from('delivery_settings')
        .update(deliveryData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating delivery settings:', error);
        throw error;
      }
      result = data;
      console.log('✅ Delivery settings updated successfully:', result);
    }

    // Verify the data was saved by fetching it back
    const { data: verifyData, error: verifyError } = await supabase
      .from('delivery_settings')
      .select('*')
      .eq('id', result.id)
      .single();

    if (verifyError) {
      console.warn('Could not verify saved data:', verifyError);
    } else {
      console.log('✅ Verified: Data saved correctly in Supabase:', verifyData);
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Error in saveDeliverySettings:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    });
    return { data: null, error };
  }
};

